import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { isSuperAdmin, isUltraAdmin } from '../data/superAdmins';
import {
  LayoutDashboard, Users, FolderOpen, FileText, Bell,
  StickyNote, Activity, Settings, Upload, User, CheckSquare,
  Zap, BarChart2, BarChart3, TrendingUp, HelpCircle, Key, Building2, Calculator, GraduationCap, CreditCard, MessageSquare, Search, BookOpen, GitCompare, Mail, Send, Shield, Timer
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'https://api.orbit-technology.com/api';

const REP_LINKS = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/reps', label: 'Teams', icon: Users, hidden: true },
  { path: '/clients', label: 'Clients', icon: Users },
  { path: '/upload', label: 'Secure Upload', icon: Upload, hidden: true },
  { path: '/documents', label: 'Documents', icon: FolderOpen, hidden: true },
  { path: '/messages', label: 'Messages', icon: MessageSquare, badge: true, hidden: true },
  { path: '/messaging', label: 'Messaging', icon: Mail, inboxBadge: true },
  { path: '/underwriting', label: 'Underwriting', icon: Zap, adminOnly: true, hidden: true },
  { path: '/activity', label: 'Activity', icon: Activity, adminOnly: true },
  { path: '/training', label: 'Training', icon: GraduationCap },
  { path: '/client-data', label: 'Client Data', icon: BarChart2, adminOnly: true },
  { path: '/rep-data', label: 'Orbit Rep Data', icon: TrendingUp, adminOnly: true, hidden: true },
  { path: '/waymo-data', label: 'Waymo Data', icon: BarChart2, adminOnly: true, hidden: true },
  { path: '/full-client-data', label: 'Full Rep Data', icon: TrendingUp, adminOnly: true, hidden: true },
  { path: '/users', label: 'User Management', icon: Users, adminOnly: true, hidden: true },
  { path: '/client-credentials', label: 'Client Credentials', icon: Key, adminOnly: true, hidden: true },
  { path: '/client-logins', label: 'Client Logins', icon: Key },
  { path: '/my-orbit', label: 'My Orbit', icon: User },
  { path: '/nexus-bot', label: 'Nexus Bot', icon: Zap, adminOnly: true, hidden: true },
  { path: '/auto-submit', label: 'Auto Submit', icon: Send, adminOnly: true, hidden: true },
// Submissions sidebar (Admin-only)  lists prior auto-submit events and allows resending
{ path: '/submissions', label: 'Submissions', icon: CheckSquare, adminOnly: true },
{ path: '/lender-metrics', label: 'Lender Metrics', icon: BarChart3, adminOnly: true },
{ path: '/email-eagle', label: 'Email Eagle', icon: Mail, adminOnly: true, hidden: true },
// Requests raised from the "Check Orbit" CRM widget's Notify Admins button
// (Admin-only) - see GET /api/notify/admin-requests.
// Submission Metrics - the clean shop leaderboard and the two queue timers.
// Second to last, immediately above Admin. superAdminOnly, not adminOnly: it
// shows every rep's numbers side by side, which is not something a regular
// admin should be browsing.
{ path: '/submission-metrics', label: 'Submission Metrics', icon: Timer, superAdminOnly: true },
{ path: '/admin', label: 'Admin', icon: Shield, adminOnly: true, adminBadge: true },
];

const CLIENT_LINKS = [
  { path: '/upload', label: 'Upload Center', icon: Upload },
  { path: '/my-documents', label: 'My Documents', icon: FileText },
  { path: '/requests', label: 'Requests', icon: Bell },
  { path: '/profile', label: 'Profile', icon: User, hidden: true },
  { path: '/messages', label: 'Messages', icon: MessageSquare, badge: true, hidden: true },
  { path: '/my-loc', label: 'Line of Credit', icon: CreditCard, locOnly: true, hidden: true },
  { path: '/businesses', label: 'My Businesses', icon: Building2, hidden: true },
  { path: '/', label: 'Dashboard', icon: LayoutDashboard, hidden: true },
];

export default function Sidebar({ mobileOpen, onClose }) {
  const { viewMode, user, token } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const isAdmin = user?.role === 'admin';
  // Ultra admins sit above super admins, so they see it too - leaving them out
  // would hide the queue's own numbers from the two people who run it.
  const canSeeSuperAdmin = isSuperAdmin(user) || isUltraAdmin(user);
  const baseLinks = viewMode === 'client' ? CLIENT_LINKS : REP_LINKS;
  const LOC_EMAILS = ['christopher.cranton@gmail.com'];
  const hasLoc = LOC_EMAILS.includes(user?.email?.toLowerCase());
  const links = baseLinks.filter(l => {
    if (l.adminOnly && !isAdmin) return false;
    if (l.superAdminOnly && !canSeeSuperAdmin) return false;
    if (l.locOnly && !hasLoc) return false;
    if (l.hidden) return false;
    return true;
  });

  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!token) return;
    const fetchUnread = () => {
      fetch(`${API}/messages/conversations`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : [])
        .then(convos => {
          const total = (Array.isArray(convos) ? convos : []).reduce((sum, c) => sum + (c.unread || 0), 0);
          setUnreadCount(total);
        })
        .catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000); // poll every 30s
    return () => clearInterval(interval);
  }, [token]);

  // Count of OPEN (not yet completed) requests on the Admin tab, shown as a
  // yellow badge next to that link - same polling pattern as the messages
  // unread count above, just admin-only and a different color/source.
  const [openAdminCount, setOpenAdminCount] = useState(0);

  useEffect(() => {
    if (!token || !isAdmin) return;
    const fetchOpenAdminCount = () => {
      fetch(`${API}/notify/admin-requests`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : [])
        .then(list => {
          const open = (Array.isArray(list) ? list : []).filter(r => !r.completedAt).length;
          setOpenAdminCount(open);
        })
        .catch(() => {});
    };
    fetchOpenAdminCount();
    const interval = setInterval(fetchOpenAdminCount, 30000);
    return () => clearInterval(interval);
  }, [token, isAdmin]);

  // Unread replies in the rep's Messaging inbox - same polling pattern again.
  const [inboxUnread, setInboxUnread] = useState(0);

  useEffect(() => {
    if (!token || !user || user.role === 'client') return;
    const fetchInboxUnread = () => {
      fetch(`${API}/messaging/threads/unread`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(d => setInboxUnread((d && d.unread) || 0))
        .catch(() => {});
    };
    fetchInboxUnread();
    const interval = setInterval(fetchInboxUnread, 30000);
    return () => clearInterval(interval);
  }, [token, user?.role]);

  const isClient = viewMode === 'client';

  return (
    <>
      {/* Overlay on mobile when open */}
      {mobileOpen && <div className="fixed inset-0 bg-slate-900/25 backdrop-blur-[2px] z-30 md:hidden transition-opacity" onClick={onClose} />}

      <div
        className={`${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 w-52 bg-white flex flex-col shrink-0 fixed md:relative h-full z-40 transition-transform duration-200`}
        style={{ borderRight: '1px solid var(--line)' }}
      >
      <div className="flex-1 overflow-y-auto py-4 px-2.5">
        <nav className="space-y-0.5">
          {links.map(({ path, label, icon: Icon, badge, adminBadge, inboxBadge }) => {
            const active = location.pathname === path;
            return (
              <Link
                key={path}
                to={path}
                onClick={onClose}
                className={`group relative flex items-center gap-2.5 px-3 py-2.5 min-h-[42px] rounded-xl text-sm transition-all duration-150 ${
                  active
                    ? 'font-semibold'
                    : 'font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                }`}
                style={active ? {
                  color: 'var(--brand-600)',
                  background: 'linear-gradient(90deg, var(--brand-50), rgba(239,246,255,0.35))',
                  boxShadow: 'inset 0 0 0 1px var(--brand-100)',
                } : undefined}
              >
                {/* Active rail — a precise 2px marker rather than a full-block highlight */}
                {active && (
                  <span
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full"
                    style={{ background: 'linear-gradient(180deg, var(--brand-500), var(--brand-700))' }}
                  />
                )}
                <Icon
                  size={15}
                  className={active ? '' : 'text-gray-400 group-hover:text-gray-600 transition-colors'}
                  style={active ? { color: 'var(--brand-600)' } : undefined}
                />
                <span className="truncate">{t(label)}</span>
                {badge && unreadCount > 0 && (
                  <span
                    className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full text-white text-[10px] flex items-center justify-center font-bold"
                    style={{ background: 'linear-gradient(180deg, #f87171, #dc2626)', boxShadow: 'var(--shadow-xs)' }}
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
                {adminBadge && openAdminCount > 0 && (
                  <span
                    className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full text-white text-[10px] flex items-center justify-center font-bold"
                    style={{ background: 'linear-gradient(180deg, #fbbf24, #d97706)', boxShadow: 'var(--shadow-xs)' }}
                  >
                    {openAdminCount > 9 ? '9+' : openAdminCount}
                  </span>
                )}
                {inboxBadge && inboxUnread > 0 && (
                  <span
                    className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full text-white text-[10px] flex items-center justify-center font-bold"
                    style={{ background: 'linear-gradient(180deg, #f87171, #dc2626)', boxShadow: 'var(--shadow-xs)' }}
                  >
                    {inboxUnread > 9 ? '9+' : inboxUnread}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
      </div>
      {isClient && (
        <div className="mx-2.5 mb-3 p-3.5 rounded-xl" style={{ background: 'var(--ink-50)', border: '1px solid var(--line)' }}>
          <div className="flex items-center gap-2 mb-1">
            <HelpCircle size={13} style={{ color: 'var(--brand-500)' }} />
            <span className="text-xs font-semibold" style={{ color: 'var(--ink-700)' }}>Need help?</span>
          </div>
          <p className="text-[11px] leading-relaxed o-muted">Contact your rep or email support@capital-infusion.com</p>
        </div>
      )}
      </div>
    </>
  );
}
